import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Save, ExternalLink } from "lucide-react";

const SLUGS = [
  { value: "home", label: "Home", visitorPath: "/" },
  { value: "about", label: "About", visitorPath: "/about" },
  { value: "contact", label: "Contact", visitorPath: "/contact" },
  { value: "booking", label: "Booking", visitorPath: "/booking" },
  { value: "events_intro", label: "Events", visitorPath: "/events" },
];

export default function AdminPages() {
  const [slug, setSlug] = useState("home");
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async (s) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/pages/${s}`);
      setPage(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(slug);
  }, [slug]);

  const updateField = (lng, field, value) => {
    setPage((p) => {
      const sections = { ...(p?.sections || {}) };
      sections[lng] = { ...(sections[lng] || {}), [field]: value };
      return { ...p, sections };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/pages/${slug}`, { sections: page.sections });
      setPage(data);
      toast.success("Page saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-pages-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">CMS</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Pages</h1>
          </div>
          <div className="flex items-center gap-3">
            <Select value={slug} onValueChange={setSlug}>
              <SelectTrigger className="w-44" data-testid="pages-slug-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SLUGS.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`pages-option-${s.value}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(() => {
              const cur = SLUGS.find((s) => s.value === slug);
              return cur?.visitorPath ? (
                <a
                  href={cur.visitorPath}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded px-1"
                  data-testid={`pages-view-as-visitor-${cur.value}`}
                  title={`Open ${cur.visitorPath} in a new tab`}
                >
                  View as visitor <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : null;
            })()}
            <Button
              onClick={save}
              disabled={saving || !page}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="pages-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
            </Button>
          </div>
        </div>

        {loading || !page ? (
          <div className="py-16 flex justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="hr">
            <TabsList data-testid="pages-locale-tabs">
              <TabsTrigger value="hr" data-testid="pages-tab-hr">Hrvatski</TabsTrigger>
              <TabsTrigger value="en" data-testid="pages-tab-en">English</TabsTrigger>
            </TabsList>
            {["hr", "en"].map((lng) => {
              const sec = page.sections?.[lng] || {};
              return (
                <TabsContent value={lng} key={lng} className="mt-6 space-y-5" data-testid={`pages-content-${lng}`}>
                  {slug === "home" ? (
                    <>
                      <div className="space-y-2">
                        <Label>Hero title</Label>
                        <Input
                          value={sec.hero_title || ""}
                          onChange={(e) => updateField(lng, "hero_title", e.target.value)}
                          data-testid={`pages-${lng}-hero-title`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Hero subtitle</Label>
                        <Textarea
                          rows={2}
                          value={sec.hero_subtitle || ""}
                          onChange={(e) => updateField(lng, "hero_subtitle", e.target.value)}
                          data-testid={`pages-${lng}-hero-subtitle`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CTA label</Label>
                        <Input
                          value={sec.cta_label || ""}
                          onChange={(e) => updateField(lng, "cta_label", e.target.value)}
                          data-testid={`pages-${lng}-cta-label`}
                        />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>About preview title</Label>
                          <Input
                            value={sec.about_preview_title || ""}
                            onChange={(e) => updateField(lng, "about_preview_title", e.target.value)}
                            data-testid={`pages-${lng}-about-title`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Menu teaser title</Label>
                          <Input
                            value={sec.menu_teaser_title || ""}
                            onChange={(e) => updateField(lng, "menu_teaser_title", e.target.value)}
                          />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label>About preview body</Label>
                          <Textarea
                            rows={4}
                            value={sec.about_preview_body || ""}
                            onChange={(e) => updateField(lng, "about_preview_body", e.target.value)}
                            data-testid={`pages-${lng}-about-body`}
                          />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Menu teaser body</Label>
                          <Textarea
                            rows={3}
                            value={sec.menu_teaser_body || ""}
                            onChange={(e) => updateField(lng, "menu_teaser_body", e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Events teaser title</Label>
                          <Input
                            value={sec.events_teaser_title || ""}
                            onChange={(e) => updateField(lng, "events_teaser_title", e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Events teaser body</Label>
                          <Textarea
                            rows={3}
                            value={sec.events_teaser_body || ""}
                            onChange={(e) => updateField(lng, "events_teaser_body", e.target.value)}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>Page title</Label>
                        <Input
                          value={sec.title || ""}
                          onChange={(e) => updateField(lng, "title", e.target.value)}
                          data-testid={`pages-${lng}-title`}
                        />
                      </div>
                      {slug === "contact" && (
                        <div className="space-y-2">
                          <Label>Form intro line</Label>
                          <Input
                            value={sec.form_intro || ""}
                            onChange={(e) => updateField(lng, "form_intro", e.target.value)}
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>Body</Label>
                        <RichTextEditor
                          value={sec.body_html || ""}
                          onChange={(html) => updateField(lng, "body_html", html)}
                          testid={`pages-rte-${lng}`}
                        />
                      </div>
                    </>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </div>
    </AdminLayout>
  );
}
